import React from 'react';
import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type {} from './augment';
import { mix, sbg, SBG_HEX } from './sbgTokens';
import {
  alertIcons,
  CalendarGlyph,
  CheckboxCheckedIcon,
  CheckboxIcon,
  CheckboxIndeterminateIcon,
  ChipDeleteIcon,
  ClearGlyph,
  NextGlyph,
  PrevGlyph,
  RadioCheckedIcon,
  RadioIcon,
  SelectChevron,
  SortArrow,
} from './controlIcons';

/*
 * SBG Funding 2026 MUI theme (book §18.6), Compact density (§4.2) for this
 * internal tool. Palette values feed MUI internals in both color schemes;
 * every component override reads the `--sbg-*` CSS variables, so styles
 * follow `data-theme="dark"` on <html> with no per-scheme code.
 */

const h = React.createElement;

const DISPLAY = '"Host Grotesk", "Host Grotesk Variable", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const BODY = '"Instrument Sans", "Instrument Sans Variable", ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const MONO = 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

const type = (name: string) => ({ font: `var(--sbg-type-${name})`, letterSpacing: `var(--sbg-type-${name}-tracking)` });
const focusVisible = { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: 2 };
const insetFocus = { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 };
const ease = (...props: string[]) => props.map((p) => `${p} ${sbg.duration.fast} ${sbg.ease.standard}`).join(', ');

type Tone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';
const toneFor = (color?: string): Tone => {
  switch (color) {
    case 'primary':
    case 'info':
      return 'info';
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'danger';
    default:
      return 'neutral';
  }
};

const MENU_PAPER = {
  backgroundColor: sbg.bg.raised,
  backgroundImage: 'none',
  border: `1px solid ${sbg.border.default}`,
  borderRadius: sbg.radius.lg,
  boxShadow: sbg.shadow.md,
};

export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-theme' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#006BF1', light: '#3389F4', dark: '#0056C1', contrastText: '#FFFFFF' },
        secondary: { main: '#002B60', light: '#344465', dark: '#00214B', contrastText: '#FFFFFF' },
        error: { main: '#CC0000', light: '#F26A5F', dark: '#9C1B1B', contrastText: '#FFFFFF' },
        warning: { main: '#FFA100', light: '#FFB15E', dark: '#895203', contrastText: '#002B60' },
        success: { main: '#17843F', light: '#4AC06C', dark: '#176933', contrastText: '#FFFFFF' },
        info: { main: '#006BF1', light: '#66A6F7', dark: '#004091', contrastText: '#FFFFFF' },
        volt: { main: '#F5FF33', light: '#F6FF83', dark: '#DEE70E', contrastText: '#002B60' },
        text: { primary: '#002B60', secondary: '#4B5A7C', disabled: '#A1AEC4' },
        background: { default: '#EEF2F9', paper: '#FFFFFF' },
        divider: '#DCE3EE',
        action: {
          active: '#4B5A7C',
          hover: 'rgba(0, 43, 96, 0.04)',
          hoverOpacity: 0.04,
          selected: 'rgba(0, 107, 241, 0.08)',
          selectedOpacity: 0.08,
          disabled: '#A1AEC4',
          disabledBackground: '#DCE3EE',
          focus: 'rgba(0, 107, 241, 0.12)',
        },
      },
    },
    dark: {
      palette: {
        primary: { main: '#3389F4', light: '#66A6F7', dark: '#006BF1', contrastText: '#FFFFFF' },
        secondary: { main: '#B2D3FB', light: '#CCE2FD', dark: '#66A6F7', contrastText: '#002B60' },
        error: { main: '#F26A5F', light: '#FB988D', dark: '#CC0000', contrastText: '#FFFFFF' },
        warning: { main: '#FFB15E', light: '#FEC48A', dark: '#FFA100', contrastText: '#002B60' },
        success: { main: '#4AC06C', light: '#83DC97', dark: '#22A752', contrastText: '#002B60' },
        info: { main: '#66A6F7', light: '#B2D3FB', dark: '#3389F4', contrastText: '#002B60' },
        volt: { main: '#F5FF33', light: '#F6FF83', dark: '#DEE70E', contrastText: '#002B60' },
        text: { primary: '#EEF2F9', secondary: '#A1AEC4', disabled: '#4B5A7C' },
        background: { default: '#000B18', paper: '#001530' },
        divider: '#102641',
        action: {
          active: '#A1AEC4',
          hover: 'rgba(255, 255, 255, 0.04)',
          hoverOpacity: 0.04,
          selected: 'rgba(102, 166, 247, 0.16)',
          selectedOpacity: 0.16,
          disabled: '#4B5A7C',
          disabledBackground: '#102641',
          focus: 'rgba(0, 195, 255, 0.16)',
        },
      },
    },
  },
  breakpoints: {
    // Tailwind breakpoints, shared by every SBG stack (book §8.4).
    values: { xs: 0, sm: 640, md: 768, lg: 1024, xl: 1280 },
  },
  shape: { borderRadius: 8 },
  spacing: 8,
  mixins: { toolbar: { minHeight: 56 } },
  typography: {
    fontFamily: BODY,
    fontSize: 14,
    htmlFontSize: 16,
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '2rem', lineHeight: '2.5rem', letterSpacing: '-0.015em', textWrap: 'balance' },
    h2: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '1.5rem', lineHeight: '2rem', letterSpacing: '-0.01em', textWrap: 'balance' },
    h3: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '1.25rem', lineHeight: '1.75rem', letterSpacing: '-0.005em', textWrap: 'balance' },
    h4: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem', letterSpacing: 0 },
    h5: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem', letterSpacing: 0 },
    h6: { fontFamily: DISPLAY, fontWeight: 600, fontSize: '0.875rem', lineHeight: '1.25rem', letterSpacing: 0 },
    subtitle1: { fontFamily: BODY, fontWeight: 500, fontSize: '0.875rem', lineHeight: '1.25rem' },
    subtitle2: { fontFamily: BODY, fontWeight: 500, fontSize: '0.8125rem', lineHeight: '1.125rem' },
    body1: { fontFamily: BODY, fontWeight: 400, fontSize: '0.875rem', lineHeight: '1.25rem' },
    body2: { fontFamily: BODY, fontWeight: 400, fontSize: '0.8125rem', lineHeight: '1.125rem' },
    caption: { fontFamily: BODY, fontWeight: 400, fontSize: '0.75rem', lineHeight: '1rem', letterSpacing: 0 },
    overline: { fontFamily: BODY, fontWeight: 500, fontSize: '0.75rem', lineHeight: '1rem', letterSpacing: '0.06em', textTransform: 'uppercase' },
    button: { fontFamily: BODY, fontWeight: 500, fontSize: '0.875rem', lineHeight: '1.25rem', letterSpacing: 0, textTransform: 'none' },
    displayLg: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '3rem', lineHeight: '3.5rem', letterSpacing: '-0.02em', textWrap: 'balance' },
    numericXl: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '3rem', lineHeight: '3.25rem', letterSpacing: '-0.02em' },
    numericLg: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '2rem', lineHeight: '2.5rem', letterSpacing: '-0.015em' },
    numericMd: { fontFamily: DISPLAY, fontWeight: 500, fontSize: '1.5rem', lineHeight: '2rem', letterSpacing: '-0.01em' },
    bodyLg: { fontFamily: BODY, fontWeight: 400, fontSize: '1.125rem', lineHeight: '1.75rem', textWrap: 'pretty' },
    bodyMd: { fontFamily: BODY, fontWeight: 400, fontSize: '1rem', lineHeight: '1.5rem', textWrap: 'pretty' },
    labelMd: { fontFamily: BODY, fontWeight: 500, fontSize: '0.875rem', lineHeight: '1.25rem' },
    labelSm: { fontFamily: BODY, fontWeight: 500, fontSize: '0.8125rem', lineHeight: '1.125rem' },
    labelXs: { fontFamily: BODY, fontWeight: 500, fontSize: '0.75rem', lineHeight: '1rem' },
    mono: { fontFamily: MONO, fontWeight: 400, fontSize: '0.8125rem', lineHeight: '1.25rem' },
  },
  // Cards rest flat; only floating layers cast shadows (book §8.6).
  shadows: [
    'none', 'none', 'none', 'none',
    sbg.shadow.md, sbg.shadow.md, sbg.shadow.md, sbg.shadow.md, sbg.shadow.md, sbg.shadow.md, sbg.shadow.md, sbg.shadow.md, sbg.shadow.md,
    sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg,
    sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg, sbg.shadow.lg,
  ] as Theme['shadows'],
  transitions: {
    duration: { shortest: 80, shorter: 150, short: 150, standard: 200, complex: 300, enteringScreen: 300, leavingScreen: 150 },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitTextSizeAdjust: '100%' },
        body: {
          ...type('body-sm'),
          backgroundColor: sbg.bg.canvas,
          color: sbg.text.primary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          scrollbarWidth: 'thin',
          scrollbarColor: `${sbg.border.default} transparent`,
        },
        '::selection': { backgroundColor: sbg.bg.selectedStrong, color: sbg.text.primary },
        ':focus-visible': focusVisible,
        'table, .sbg-num': { fontVariantNumeric: 'tabular-nums' },
        // Visually hidden labels inside wide tables are absolutely positioned;
        // anchoring them to the table keeps them inside its scroll container
        // instead of stretching the page sideways on phones.
        table: { position: 'relative' },
        'h1, h2, h3': { textWrap: 'balance' },
        p: { textWrap: 'pretty' },
        '::-webkit-scrollbar': { width: 10, height: 10 },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: sbg.border.default,
          borderRadius: 999,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '::-webkit-scrollbar-thumb:hover': { backgroundColor: sbg.border.strong },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        // Checkbox and radio glyphs (see theme/controlIcons.tsx).
        '.sbg-check': {
          display: 'inline-grid',
          placeItems: 'center',
          width: 16,
          height: 16,
          flex: 'none',
          boxSizing: 'border-box',
          borderRadius: 4,
          border: `1px solid ${sbg.border.strong}`,
          backgroundColor: sbg.bg.surface,
          color: sbg.text.onBrand,
          transition: ease('background-color', 'border-color'),
        },
        '.sbg-check--on': { backgroundColor: sbg.action.primary, borderColor: sbg.action.primary },
        '.sbg-check--radio': { borderRadius: '50%' },
        '.sbg-check--radio-on': { backgroundColor: sbg.bg.surface, borderColor: sbg.action.primary },
        '.sbg-check__dot': { width: 6, height: 6, borderRadius: '50%', backgroundColor: sbg.action.primary },
        '.Mui-disabled .sbg-check': { backgroundColor: sbg.bg.disabled, borderColor: sbg.border.default, color: sbg.text.disabled },
        '.Mui-disabled .sbg-check__dot': { backgroundColor: sbg.text.disabled },
        '.Mui-focusVisible .sbg-check': focusVisible,
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
        },
      },
    },
    MuiButtonBase: {
      // Ripples are a Material tell (book §16).
      defaultProps: { disableRipple: true, disableTouchRipple: true },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: ({ ownerState }) => {
          const size = ownerState.size ?? 'medium';
          const height = size === 'small' ? sbg.control.sm : size === 'large' ? sbg.control.lg : sbg.control.md;
          const padX = size === 'small' ? 12 : size === 'large' ? 20 : 16;
          const variant = ownerState.variant ?? 'text';
          const color = ownerState.color ?? 'primary';
          const base = {
            minHeight: height,
            height,
            padding: `0 ${padX}px`,
            minWidth: 0,
            gap: 8,
            borderRadius: sbg.radius.action,
            border: '1px solid transparent',
            boxShadow: 'none',
            whiteSpace: 'nowrap' as const,
            ...type(size === 'small' ? 'label-sm' : 'label-md'),
            ...(size === 'large' ? { font: `500 16px/24px ${sbg.font.body}` } : null),
            textTransform: 'none' as const,
            transition: ease('background-color', 'border-color', 'color'),
            '&:hover': { boxShadow: 'none' },
            '&.Mui-focusVisible': focusVisible,
            '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 },
            '& .MuiButton-startIcon > svg, & .MuiButton-endIcon > svg, & .MuiButton-icon > svg': {
              width: size === 'large' ? 20 : 16,
              height: size === 'large' ? 20 : 16,
            },
            '&.Mui-disabled': {
              backgroundColor: variant === 'text' ? 'transparent' : sbg.bg.disabled,
              color: sbg.text.disabled,
              borderColor: 'transparent',
            },
          };
          if (color === 'inherit') {
            return { ...base, color: 'inherit', ...(variant === 'outlined' ? { borderColor: 'currentColor' } : null) };
          }
          if (variant === 'contained') {
            if (color === 'volt') {
              return {
                ...base,
                backgroundColor: sbg.action.volt,
                color: sbg.text.onVolt,
                borderColor: sbg.action.voltBorder,
                '&:hover': { backgroundColor: sbg.action.voltHover, boxShadow: 'none' },
                '&:active': { backgroundColor: sbg.action.voltActive },
              };
            }
            if (color === 'error') {
              return {
                ...base,
                backgroundColor: sbg.action.danger,
                color: sbg.text.onBrand,
                '&:hover': { backgroundColor: sbg.action.dangerHover, boxShadow: 'none' },
              };
            }
            // Primary, info, success, warning and secondary all act as Primary Blue:
            // status colors speak only for state, never for actions (book §6.5).
            return {
              ...base,
              backgroundColor: sbg.action.primary,
              color: sbg.text.onBrand,
              '&:hover': { backgroundColor: sbg.action.primaryHover, boxShadow: 'none' },
              '&:active': { backgroundColor: sbg.action.primaryActive },
            };
          }
          if (variant === 'outlined') {
            if (color === 'error') {
              return {
                ...base,
                backgroundColor: sbg.bg.surface,
                color: sbg.status.danger.text,
                borderColor: sbg.status.danger.border,
                '&:hover': { backgroundColor: sbg.status.danger.bg, borderColor: sbg.status.danger.border },
              };
            }
            return {
              ...base,
              backgroundColor: sbg.bg.surface,
              color: sbg.text.primary,
              borderColor: sbg.action.secondaryBorder,
              '&:hover': { backgroundColor: sbg.action.secondaryHover, borderColor: sbg.action.secondaryBorder },
              '&:active': { backgroundColor: sbg.bg.selectedStrong },
            };
          }
          // Ghost.
          return {
            ...base,
            padding: `0 ${Math.max(padX - 4, 10)}px`,
            backgroundColor: 'transparent',
            color: color === 'error' ? sbg.status.danger.text : sbg.text.primary,
            '&:hover': { backgroundColor: color === 'error' ? sbg.status.danger.bg : sbg.bg.hover },
            '&:active': { backgroundColor: sbg.bg.pressed },
          };
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const size = ownerState.size ?? 'medium';
          const box = size === 'small' ? 32 : size === 'large' ? 44 : 36;
          const glyph = size === 'large' ? 20 : 18;
          const color = ownerState.color ?? 'default';
          return {
            width: box,
            height: box,
            padding: 0,
            borderRadius: '50%',
            color:
              color === 'inherit' ? 'inherit'
                : color === 'error' ? sbg.status.danger.icon
                  : color === 'primary' ? sbg.text.accent
                    : sbg.text.secondary,
            transition: ease('background-color', 'color'),
            '& > svg': { width: glyph, height: glyph },
            '&:hover': {
              backgroundColor: color === 'error' ? sbg.status.danger.bg : sbg.bg.hover,
              color: color === 'inherit' ? 'inherit' : color === 'error' ? sbg.status.danger.text : sbg.text.primary,
            },
            '&:active': { backgroundColor: sbg.bg.pressed },
            '&.Mui-focusVisible': focusVisible,
            '&.Mui-disabled': { color: sbg.text.disabled },
          };
        },
        edgeStart: { marginLeft: -8 },
        edgeEnd: { marginRight: -8 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ ownerState }) => {
          const floating = ownerState.variant !== 'outlined' && (ownerState.elevation ?? 0) >= 4;
          return {
            backgroundImage: 'none',
            backgroundColor: floating ? sbg.bg.raised : sbg.bg.surface,
            color: sbg.text.primary,
            border: `1px solid ${sbg.border.default}`,
            borderRadius: ownerState.square ? 0 : sbg.radius.card,
            boxShadow: floating ? undefined : 'none',
          };
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: sbg.bg.surface,
          border: `1px solid ${sbg.border.default}`,
          borderRadius: sbg.radius.card,
          boxShadow: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '20px 20px 0', gap: 12 },
        title: { ...type('heading-4'), color: sbg.text.primary },
        subheader: { ...type('body-xs'), color: sbg.text.secondary, marginTop: 2 },
        action: { margin: 0, alignSelf: 'center' },
        avatar: { marginRight: 0 },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
    },
    MuiCardActions: {
      styleOverrides: { root: { padding: '12px 20px', gap: 8, '& > :not(style) ~ :not(style)': { marginLeft: 0 } } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: sbg.bg.surface,
          backgroundImage: 'none',
          color: sbg.text.primary,
          border: 0,
          borderBottom: `1px solid ${sbg.border.default}`,
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: { regular: { minHeight: 56, '@media (min-width: 640px)': { minHeight: 56 } } },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ ownerState }) => {
          const temporary = ownerState.variant === 'temporary';
          const right = ownerState.anchor === 'right';
          return {
            backgroundImage: 'none',
            backgroundColor: temporary ? sbg.bg.raised : sbg.bg.surface,
            border: 0,
            borderRadius: 0,
            boxShadow: temporary ? sbg.shadow.lg : 'none',
            ...(ownerState.variant === 'permanent' || ownerState.variant === 'persistent'
              ? { borderRight: `1px solid ${sbg.border.default}` }
              : null),
            ...(temporary && right
              ? {
                  borderTopLeftRadius: sbg.radius.panel,
                  borderBottomLeftRadius: sbg.radius.panel,
                  '@media (max-width: 639.95px)': { borderRadius: 0 },
                }
              : null),
          };
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: sbg.bg.overlay } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: sbg.bg.raised,
          backgroundImage: 'none',
          border: 0,
          borderRadius: sbg.radius.panel,
          boxShadow: sbg.shadow.lg,
          margin: 16,
          maxHeight: 'calc(100% - 32px)',
        },
        // A full-screen dialog still gets the paperWidthSm override below (its
        // maxWidth prop stays "sm"), and this one is applied after it, so it
        // must undo the 560 px cap or the dialog stays a narrow column.
        paperFullScreen: { margin: 0, maxWidth: '100%', maxHeight: '100%', borderRadius: 0 },
        paperWidthXs: { maxWidth: 400 },
        paperWidthSm: { maxWidth: 560 },
        paperWidthMd: { maxWidth: 720 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { ...type('heading-3'), color: sbg.text.primary, padding: '24px 24px 8px' },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { ...type('body-sm'), padding: '8px 24px 16px', color: sbg.text.primary },
        dividers: { padding: '16px 24px', borderColor: sbg.border.subtle },
      },
    },
    MuiDialogContentText: {
      styleOverrides: { root: { ...type('body-sm'), color: sbg.text.secondary } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '8px 24px 24px', gap: 8, flexWrap: 'wrap', '& > :not(style) ~ :not(style)': { marginLeft: 0 } },
      },
    },
    MuiPopover: {
      styleOverrides: { paper: MENU_PAPER },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { ...MENU_PAPER, marginTop: 4, minWidth: 200 },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...type('body-sm'),
          minHeight: 32,
          padding: '6px 8px',
          gap: 8,
          borderRadius: sbg.radius.md,
          color: sbg.text.primary,
          '&:hover': { backgroundColor: sbg.bg.hover },
          '&.Mui-focusVisible': { backgroundColor: sbg.bg.hover, ...insetFocus },
          '&.Mui-selected': {
            backgroundColor: sbg.bg.selected,
            '&:hover, &.Mui-focusVisible': { backgroundColor: sbg.bg.selectedStrong },
          },
          '&.Mui-disabled': { opacity: 1, color: sbg.text.disabled },
          '& .MuiListItemIcon-root': { minWidth: 0, color: sbg.text.secondary },
          '& .MuiListItemIcon-root > svg': { width: 16, height: 16 },
          '& + .MuiDivider-root': { margin: '4px 0' },
        },
      },
    },
    MuiList: {
      styleOverrides: { root: { '& > .MuiDivider-root': { margin: '4px 0' } } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: sbg.radius.md,
          transition: ease('background-color', 'color'),
          '&:hover': { backgroundColor: sbg.bg.hover },
          '&.Mui-focusVisible': { backgroundColor: sbg.bg.hover, ...insetFocus },
          '&.Mui-selected': {
            backgroundColor: sbg.bg.selected,
            '&:hover': { backgroundColor: sbg.bg.selectedStrong },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 32, color: sbg.text.secondary } },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { ...type('body-sm'), color: sbg.text.primary },
        secondary: { ...type('body-xs'), color: sbg.text.secondary },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          ...type('overline'),
          textTransform: 'uppercase',
          color: sbg.text.secondary,
          backgroundColor: 'transparent',
          padding: '10px 10px 4px',
        },
      },
    },
    MuiTooltip: {
      defaultProps: { enterDelay: 400, enterNextDelay: 200, arrow: false },
      styleOverrides: {
        tooltip: {
          backgroundColor: sbg.bg.inverse,
          color: sbg.text.inverse,
          font: `500 12px/16px ${sbg.font.body}`,
          borderRadius: sbg.radius.sm,
          padding: '6px 8px',
          maxWidth: 240,
        },
      },
    },
    MuiChip: {
      defaultProps: { deleteIcon: h(ChipDeleteIcon) },
      styleOverrides: {
        root: ({ ownerState }) => {
          const small = ownerState.size === 'small';
          const quiet = ownerState.variant === 'outlined' && (ownerState.color ?? 'default') === 'default';
          const tone = sbg.status[toneFor(ownerState.color)];
          return {
            height: small ? 22 : 32,
            borderRadius: sbg.radius.pill,
            border: `1px solid ${quiet ? sbg.border.default : tone.border}`,
            backgroundColor: quiet ? sbg.bg.surface : tone.bg,
            color: quiet ? sbg.text.primary : tone.text,
            ...type(small ? 'label-xs' : 'label-sm'),
            maxWidth: '100%',
            transition: ease('background-color', 'border-color'),
            '&.MuiChip-clickable:hover': {
              backgroundColor: quiet ? sbg.bg.hover : mix(tone.border, 45, tone.bg),
            },
            '&.MuiChip-clickable:active': { boxShadow: 'none' },
            '&.Mui-focusVisible': { ...focusVisible, backgroundColor: quiet ? sbg.bg.hover : tone.bg },
            '&.Mui-disabled': { opacity: 1, color: sbg.text.disabled, borderColor: sbg.border.subtle, backgroundColor: sbg.bg.surfaceSubtle },
          };
        },
        label: ({ ownerState }) => ({ padding: ownerState.size === 'small' ? '0 8px' : '0 12px' }),
        icon: ({ ownerState }) => ({
          color: 'inherit',
          width: ownerState.size === 'small' ? 12 : 16,
          height: ownerState.size === 'small' ? 12 : 16,
          fontSize: ownerState.size === 'small' ? 12 : 16,
          margin: ownerState.size === 'small' ? '0 -4px 0 8px' : '0 -6px 0 10px',
        }),
        deleteIcon: ({ ownerState }) => ({
          color: 'inherit',
          opacity: 0.7,
          width: 14,
          height: 14,
          margin: ownerState.size === 'small' ? '0 6px 0 -4px' : '0 8px 0 -6px',
          '&:hover': { color: 'inherit', opacity: 1 },
        }),
        avatar: { width: 18, height: 18, marginLeft: 6, marginRight: -4, fontSize: 10 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { font: `600 13px/1 ${sbg.font.body}`, boxShadow: 'inset 0 0 0 1px rgba(0, 43, 96, 0.08)' },
        colorDefault: { backgroundColor: sbg.bg.avatar, color: sbg.text.avatar },
      },
    },
    MuiAvatarGroup: {
      styleOverrides: { avatar: { border: `2px solid ${sbg.bg.surface}`, marginLeft: -6 } },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { ...type('label-xs'), height: 18, minWidth: 18, padding: '0 5px', borderRadius: sbg.radius.pill, fontVariantNumeric: 'tabular-nums' },
        colorPrimary: { backgroundColor: sbg.action.primary, color: sbg.text.onBrand },
        colorError: { backgroundColor: sbg.action.danger, color: sbg.text.onBrand },
        dot: { height: 8, minWidth: 8, width: 8, padding: 0, boxShadow: `0 0 0 2px ${sbg.bg.surface}` },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: sbg.border.subtle } },
    },
    MuiLink: {
      defaultProps: { underline: 'always' },
      styleOverrides: {
        root: {
          color: sbg.text.link,
          textDecorationColor: 'currentColor',
          textUnderlineOffset: 2,
          textDecorationThickness: 1,
          cursor: 'pointer',
          '&:hover': { color: sbg.text.linkHover, textDecorationColor: 'currentColor' },
          '&.Mui-focusVisible, &:focus-visible': { ...focusVisible, borderRadius: 2 },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          ...type('body-sm'),
          color: sbg.text.primary,
          '&.Mui-disabled': { color: sbg.text.disabled },
        },
        input: ({ ownerState }) => ({
          height: ownerState.multiline ? 'auto' : 20,
          '&::placeholder': { color: sbg.text.tertiary, opacity: 1 },
          '&.Mui-disabled': { WebkitTextFillColor: sbg.text.disabled },
        }),
      },
    },
    MuiOutlinedInput: {
      defaultProps: { notched: false },
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: sbg.radius.control,
          backgroundColor: sbg.bg.surface,
          transition: ease('box-shadow'),
          ...(ownerState.multiline ? { padding: '8px 12px' } : null),
          '& .MuiOutlinedInput-notchedOutline': {
            top: 0,
            borderColor: sbg.border.strong,
            borderWidth: 1,
            transition: ease('border-color'),
            '& legend': { display: 'none' },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: sbg.border.hover },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${sbg.focus.ringSoft}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sbg.focus.ring, borderWidth: 1 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: sbg.status.danger.icon },
          '&.Mui-error.Mui-focused': { boxShadow: `0 0 0 3px ${mix(sbg.status.danger.icon, 24)}` },
          '&.Mui-disabled': {
            backgroundColor: sbg.bg.surfaceSubtle,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: sbg.border.default },
          },
          '&.MuiInputBase-adornedStart': { paddingLeft: 12 },
          '&.MuiInputBase-adornedEnd': { paddingRight: 8 },
        }),
        input: ({ ownerState }) => ({
          padding: ownerState.multiline ? 0 : ownerState.size === 'small' ? '8px 12px' : '12px 14px',
          '&.MuiInputBase-inputAdornedStart': { paddingLeft: 0 },
          '&.MuiInputBase-inputAdornedEnd': { paddingRight: 0 },
        }),
      },
    },
    MuiInputLabel: {
      // Labels sit above the field, always visible (book §11.4) — no floating labels.
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          ...type('label-md'),
          position: 'relative',
          transform: 'none',
          maxWidth: '100%',
          overflow: 'visible',
          whiteSpace: 'normal',
          marginBottom: 6,
          color: sbg.text.primary,
          '&.Mui-focused, &.Mui-error': { color: sbg.text.primary },
          '&.Mui-disabled': { color: sbg.text.disabled },
          '& .MuiInputLabel-asterisk, & .MuiFormLabel-asterisk': { color: sbg.text.secondary },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...type('label-md'),
          color: sbg.text.primary,
          '&.Mui-focused, &.Mui-error': { color: sbg.text.primary },
          '&.Mui-disabled': { color: sbg.text.disabled },
        },
        asterisk: { color: sbg.text.secondary },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...type('body-xs'),
          margin: '6px 0 0',
          color: sbg.text.secondary,
          '&.Mui-error': { color: sbg.status.danger.text },
        },
      },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { marginLeft: -4, marginRight: 16, gap: 4, '& .MuiSwitch-root': { marginLeft: 4, marginRight: 6 } },
        label: { ...type('body-sm'), color: sbg.text.primary, '&.Mui-disabled': { color: sbg.text.disabled } },
      },
    },
    MuiSelect: {
      defaultProps: { IconComponent: SelectChevron },
      styleOverrides: {
        select: { '&:focus': { backgroundColor: 'transparent' }, paddingRight: '36px !important' },
        icon: { color: sbg.text.secondary, right: 12, top: 'calc(50% - 8px)', width: 16, height: 16 },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: { color: sbg.text.tertiary, '& > svg': { width: 16, height: 16 } },
        positionStart: { marginRight: 8 },
        positionEnd: { marginLeft: 8 },
      },
    },
    MuiAutocomplete: {
      defaultProps: { popupIcon: h(SelectChevron), clearIcon: h(ClearGlyph) },
      styleOverrides: {
        paper: { ...MENU_PAPER, marginTop: 4 },
        listbox: {
          padding: 4,
          '& .MuiAutocomplete-option': {
            ...type('body-sm'),
            minHeight: 32,
            padding: '6px 8px',
            borderRadius: sbg.radius.md,
            '&.Mui-focused': { backgroundColor: sbg.bg.hover },
            '&[aria-selected="true"]': { backgroundColor: sbg.bg.selected },
            '&[aria-selected="true"].Mui-focused': { backgroundColor: sbg.bg.selectedStrong },
          },
        },
        inputRoot: {
          '&.MuiInputBase-sizeSmall': { padding: '4px 36px 4px 8px' },
          '&.MuiInputBase-sizeSmall .MuiAutocomplete-input': { padding: '4px 4px' },
        },
        tag: { margin: 2 },
        noOptions: { ...type('body-sm'), color: sbg.text.secondary },
        loading: { ...type('body-sm'), color: sbg.text.secondary },
        groupLabel: { ...type('overline'), textTransform: 'uppercase', color: sbg.text.secondary, backgroundColor: sbg.bg.raised },
        popupIndicator: { width: 24, height: 24, color: sbg.text.secondary },
        clearIndicator: { width: 24, height: 24, color: sbg.text.secondary },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: h(CheckboxIcon),
        checkedIcon: h(CheckboxCheckedIcon),
        indeterminateIcon: h(CheckboxIndeterminateIcon),
        disableRipple: true,
      },
      styleOverrides: {
        root: { padding: 4, borderRadius: 6, '&:hover': { backgroundColor: 'transparent' } },
      },
    },
    MuiRadio: {
      defaultProps: { icon: h(RadioIcon), checkedIcon: h(RadioCheckedIcon), disableRipple: true },
      styleOverrides: {
        root: { padding: 4, '&:hover': { backgroundColor: 'transparent' } },
      },
    },
    MuiSwitch: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: { width: 36, height: 20, padding: 0, overflow: 'visible' },
        switchBase: {
          padding: 2,
          color: '#FFFFFF',
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#FFFFFF',
            '& + .MuiSwitch-track': { backgroundColor: sbg.action.primary, opacity: 1 },
            '&:hover': { backgroundColor: 'transparent' },
          },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.45 },
          '&.Mui-disabled .MuiSwitch-thumb': { color: SBG_HEX.slate25 },
          '&.Mui-focusVisible .MuiSwitch-thumb': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: 2 },
        },
        thumb: { width: 16, height: 16, boxShadow: sbg.shadow.xs },
        track: {
          borderRadius: sbg.radius.pill,
          backgroundColor: sbg.bg.switchOff,
          opacity: 1,
          transition: ease('background-color'),
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          padding: 3,
          gap: 2,
          backgroundColor: sbg.bg.surface,
          border: `1px solid ${sbg.border.default}`,
          borderRadius: sbg.radius.pill,
          width: 'fit-content',
          maxWidth: '100%',
        },
        grouped: { '&&': { border: 0, borderRadius: sbg.radius.pill, margin: 0 } },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...type('label-sm'),
          height: ownerState.size === 'small' ? 26 : 30,
          padding: ownerState.size === 'small' ? '0 10px' : '0 14px',
          gap: 6,
          border: 0,
          borderRadius: sbg.radius.pill,
          textTransform: 'none',
          color: sbg.text.secondary,
          transition: ease('background-color', 'color'),
          '& > svg': { width: 16, height: 16 },
          '&:hover': { color: sbg.text.primary, backgroundColor: 'transparent' },
          '&.Mui-selected': {
            backgroundColor: sbg.action.primary,
            color: sbg.text.onBrand,
            '&:hover': { backgroundColor: sbg.action.primaryHover, color: sbg.text.onBrand },
          },
          '&.Mui-focusVisible': focusVisible,
          '&.Mui-disabled': { color: sbg.text.disabled, border: 0 },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40, boxShadow: `inset 0 -1px 0 ${sbg.border.default}` },
        flexContainer: { gap: 24 },
        indicator: { height: 2, borderRadius: 2, backgroundColor: sbg.action.primary },
        scrollButtons: { width: 32, color: sbg.text.secondary, '&.Mui-disabled': { opacity: 0.3 } },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...type('label-md'),
          minHeight: 40,
          minWidth: 0,
          padding: 0,
          gap: 6,
          textTransform: 'none',
          color: sbg.text.secondary,
          transition: ease('color'),
          '&:hover': { color: sbg.text.primary },
          '&.Mui-selected': { color: sbg.text.accent },
          '&.Mui-focusVisible': { ...insetFocus, borderRadius: 4 },
          '& > svg': { width: 16, height: 16 },
          '&.MuiTab-labelIcon': { minHeight: 40 },
          '& .MuiTab-iconWrapper': { margin: 0 },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '&.MuiPaper-root': { borderRadius: sbg.radius.lg, border: `1px solid ${sbg.border.default}`, boxShadow: 'none' },
        },
      },
    },
    MuiTable: {
      styleOverrides: { root: { fontVariantNumeric: 'tabular-nums', borderCollapse: 'separate', borderSpacing: 0 } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          ...type('body-sm'),
          color: sbg.text.primary,
          padding: '10px 12px',
          borderBottom: `1px solid ${sbg.border.subtle}`,
          '&:first-of-type': { paddingLeft: 16 },
          '&:last-of-type': { paddingRight: 16 },
        },
        head: {
          ...type('label-xs'),
          height: 36,
          padding: '0 12px',
          color: sbg.text.secondary,
          backgroundColor: sbg.bg.surfaceSubtle,
          borderBottom: `1px solid ${sbg.border.default}`,
          whiteSpace: 'nowrap',
          textTransform: 'none',
          letterSpacing: 0,
        },
        sizeSmall: { padding: '6px 12px' },
        paddingCheckbox: { width: 44, padding: '0 0 0 12px', '&:first-of-type': { paddingLeft: 12 } },
        footer: { ...type('label-sm'), color: sbg.text.primary, borderTop: `1px solid ${sbg.border.strong}`, borderBottom: 0 },
        stickyHeader: { backgroundColor: sbg.bg.surfaceSubtle },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: ease('background-color'),
          '.MuiTableBody-root &:hover': { backgroundColor: sbg.bg.hover },
          '.MuiTableBody-root &:last-of-type > .MuiTableCell-root': { borderBottom: 0 },
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: sbg.bg.selected },
          '&.Mui-focusVisible, &:focus-visible': insetFocus,
        },
      },
    },
    MuiTableSortLabel: {
      defaultProps: { IconComponent: SortArrow },
      styleOverrides: {
        root: {
          color: 'inherit',
          '&:hover': { color: sbg.text.primary },
          '&.Mui-active': { color: sbg.text.primary },
          '&.Mui-active .MuiTableSortLabel-icon': { color: sbg.action.primary },
          '&.Mui-focusVisible': { ...focusVisible, borderRadius: 2 },
        },
        icon: { margin: '0 0 0 4px', width: 14, height: 14 },
      },
    },
    MuiTablePagination: {
      defaultProps: { slotProps: { actions: { previousButton: { size: 'small' }, nextButton: { size: 'small' } } } },
      styleOverrides: {
        root: { ...type('body-xs'), color: sbg.text.secondary, borderBottom: 0, borderTop: `1px solid ${sbg.border.subtle}` },
        toolbar: { minHeight: 48, paddingLeft: 16, paddingRight: 8, '@media (min-width: 640px)': { minHeight: 48 } },
        selectLabel: { ...type('body-xs'), color: sbg.text.secondary },
        displayedRows: { ...type('body-xs'), color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' },
        select: { ...type('body-xs') },
        selectIcon: { color: sbg.text.secondary },
      },
    },
    MuiAlert: {
      defaultProps: { iconMapping: alertIcons },
      styleOverrides: {
        root: ({ ownerState }) => {
          const tone = sbg.status[toneFor(ownerState.severity ?? ownerState.color ?? 'success')];
          return {
            ...type('body-sm'),
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px 16px',
            borderRadius: sbg.radius.lg,
            border: `1px solid ${tone.border}`,
            backgroundColor: tone.bg,
            color: tone.text,
            '& .MuiAlert-icon': { color: tone.icon },
            '.MuiSnackbar-root &': {
              backgroundColor: sbg.bg.raised,
              borderColor: sbg.border.default,
              color: sbg.text.primary,
              boxShadow: sbg.shadow.md,
              minWidth: 300,
              maxWidth: 400,
            },
          };
        },
        icon: { padding: 0, marginRight: 0, opacity: 1, '& > svg': { width: 20, height: 20 } },
        message: { padding: 0, minWidth: 0, overflow: 'visible', flex: 1 },
        action: { padding: 0, marginRight: -6, marginTop: -6, alignItems: 'flex-start' },
      },
    },
    MuiAlertTitle: {
      styleOverrides: { root: { ...type('label-md'), margin: '0 0 2px', color: 'inherit' } },
    },
    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } },
      styleOverrides: {
        root: { '@media (min-width: 640px)': { bottom: 24, right: 24 } },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          ...type('body-sm'),
          backgroundColor: sbg.bg.raised,
          color: sbg.text.primary,
          border: `1px solid ${sbg.border.default}`,
          borderRadius: sbg.radius.lg,
          boxShadow: sbg.shadow.md,
          padding: '8px 12px 8px 16px',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: sbg.radius.pill,
          '&, &.MuiLinearProgress-colorPrimary, &.MuiLinearProgress-colorSecondary, &.MuiLinearProgress-colorSuccess, &.MuiLinearProgress-colorWarning, &.MuiLinearProgress-colorError, &.MuiLinearProgress-colorInfo':
            { backgroundColor: sbg.bg.track },
          '&.MuiLinearProgress-colorPrimary .MuiLinearProgress-bar, &.MuiLinearProgress-colorInfo .MuiLinearProgress-bar': { backgroundColor: sbg.action.primary },
          '&.MuiLinearProgress-colorSecondary .MuiLinearProgress-bar': { backgroundColor: sbg.palette.blue[800] },
          '&.MuiLinearProgress-colorSuccess .MuiLinearProgress-bar': { backgroundColor: sbg.status.success.icon },
          '&.MuiLinearProgress-colorWarning .MuiLinearProgress-bar': { backgroundColor: sbg.palette.warning[500] },
          '&.MuiLinearProgress-colorError .MuiLinearProgress-bar': { backgroundColor: sbg.status.danger.icon },
        },
        bar: { borderRadius: sbg.radius.pill },
      },
    },
    MuiCircularProgress: {
      styleOverrides: { colorPrimary: { color: sbg.action.primary } },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { backgroundColor: sbg.bg.skeleton, borderRadius: sbg.radius.sm },
        rounded: { borderRadius: sbg.radius.md },
        wave: {
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${sbg.bg.skeletonShine}, transparent)`,
            animationDuration: '1.4s',
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: sbg.bg.surface,
          border: `1px solid ${sbg.border.default}`,
          '&, &:first-of-type, &:last-of-type': { borderRadius: sbg.radius.lg },
          overflow: 'hidden',
          '&::before': { display: 'none' },
          '& + &': { marginTop: 8 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { ...type('label-md'), minHeight: 48, padding: '0 16px', '&.Mui-focusVisible': { backgroundColor: sbg.bg.hover } },
        content: { margin: '12px 0' },
        expandIconWrapper: { color: sbg.text.secondary },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: { root: { padding: '0 16px 16px' } },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { ...type('body-xs'), color: sbg.text.secondary },
        separator: { color: sbg.palette.slate[300], margin: '0 6px' },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          ...type('label-sm'),
          borderRadius: sbg.radius.pill,
          color: sbg.text.primary,
          '&:hover': { backgroundColor: sbg.bg.hover },
          '&.Mui-selected': {
            backgroundColor: sbg.action.primary,
            color: sbg.text.onBrand,
            '&:hover': { backgroundColor: sbg.action.primaryHover },
          },
          '&.Mui-focusVisible': focusVisible,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: sbg.border.strong,
          '&.Mui-active, &.Mui-completed': { color: sbg.action.primary },
        },
        text: { font: `500 12px/1 ${sbg.font.body}` },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          ...type('label-sm'),
          color: sbg.text.secondary,
          '&.Mui-active, &.Mui-completed': { color: sbg.text.primary, fontWeight: 500 },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          displayLg: 'h1',
          numericXl: 'div',
          numericLg: 'div',
          numericMd: 'div',
          bodyLg: 'p',
          bodyMd: 'p',
          labelMd: 'span',
          labelSm: 'span',
          labelXs: 'span',
          mono: 'span',
          overline: 'span',
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        slots: {
          openPickerIcon: CalendarGlyph,
          switchViewIcon: SelectChevron,
          leftArrowIcon: PrevGlyph,
          rightArrowIcon: NextGlyph,
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: { paper: { ...MENU_PAPER, boxShadow: sbg.shadow.md } },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          ...type('body-sm'),
          borderRadius: sbg.radius.pill,
          color: sbg.text.primary,
          '&:hover': { backgroundColor: sbg.bg.hover },
          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
            backgroundColor: sbg.action.primary,
            color: sbg.text.onBrand,
          },
          '&.MuiPickersDay-today:not(.Mui-selected)': { borderColor: sbg.border.strong },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: { label: { ...type('label-md') } },
    },
    MuiDayCalendar: {
      styleOverrides: { weekDayLabel: { ...type('label-xs'), color: sbg.text.secondary } },
    },
  },
});

export default theme;
